import { NetworkStatus } from '@apollo/client';
import { useAiDraftsQuery } from 'lib-knowledge-management-common/graphql';
import { useCallback, useMemo } from 'react';

import { suggestionsPageSize } from '../utils/draftQueueCache';
import { Suggestion, toSuggestion } from '../utils/toSuggestion';

import { useCategoryNames } from './useCategoryNames';
import { useCollectionNames } from './useCollectionNames';
import { useSeedCategoryTitles } from './useSeedCategoryTitles';

export type UseSuggestions = {
  suggestions: Suggestion[];
  /** First load, as opposed to loading another page. */
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  fetchMore: () => void;
};

/**
 * One page of the AI draft queue, both kinds of draft together.
 *
 * No `type` filter: the backend orders by `COALESCE(modified_at, created_at)
 * DESC, id DESC` — the same expression `toSuggestion` reads into `activityAt` —
 * so a single offset pages the merged set and nothing needs sorting here.
 */
export function useSuggestions({
  searchQuery,
}: {
  searchQuery: string | undefined;
}): UseSuggestions {
  const { data, loading, networkStatus, fetchMore, error } = useAiDraftsQuery({
    // `filters` is left off rather than passed empty: Apollo keys the cache on
    // the variables object, and an absent key is not the same key as an
    // undefined one.
    variables: { searchQuery, limit: suggestionsPageSize, offset: 0 },
    // A draft approved or rejected elsewhere should drop out of the list.
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const results = data?.aiDrafts?.results;

  const collectionIds = useMemo(
    () => (results ?? []).map((draft) => draft.collectionId),
    [results],
  );

  const collectionNamesById = useCollectionNames(collectionIds);

  const categoryName = useCategoryNames();

  // Names are read from the cache, so the categories of every collection these
  // drafts sit in have to be in it.
  const seeded = useSeedCategoryTitles(collectionIds);

  const suggestions = useMemo(
    () =>
      (results ?? []).map((d) =>
        toSuggestion(d, collectionNamesById, categoryName),
      ),
    // `seeded` so the names re-resolve when the categories arrive.
    [results, collectionNamesById, categoryName, seeded],
  );

  const loadedCount = results?.length ?? 0;
  const onFetchMore = useCallback(() => {
    fetchMore({
      variables: { offset: loadedCount },
      // Apollo replaces the field by default, and there is no type policy
      // merging AiDraftQueryPayload, so pages have to be appended here.
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult?.aiDrafts || !previous.aiDrafts) return previous;
        // Offsets are counted against the server's list as it is now. When
        // it has moved on without the queue hearing — a draft written while
        // the socket was down — the rows loaded sit lower there, and this
        // page can start with one of them again.
        const loaded = new Set(previous.aiDrafts.results.map(({ id }) => id));
        return {
          ...previous,
          aiDrafts: {
            ...fetchMoreResult.aiDrafts,
            results: [
              ...previous.aiDrafts.results,
              ...fetchMoreResult.aiDrafts.results.filter(
                ({ id }) => !loaded.has(id),
              ),
            ],
          },
        };
      },
    });
  }, [fetchMore, loadedCount]);

  // After every hook, not straight off the query: thrown between them, an error
  // render would run a shorter list of hooks than the render before it.
  // Surfaced by KnowledgeErrorBoundary, as the articles list does.
  if (error) {
    throw new Error('useSuggestions', { cause: error });
  }

  return {
    suggestions,
    loading: loading && networkStatus !== NetworkStatus.fetchMore,
    loadingMore: networkStatus === NetworkStatus.fetchMore,
    hasMore: data?.aiDrafts?.pagination.hasMore ?? false,
    fetchMore: onFetchMore,
  };
}
